import { useSelector } from "react-redux";

export const useHeaders = (token, storeIdParam) => {
  const currentStoreId = useSelector((state) => state.current_store?.id);

  const getSubdomain = () => {
    const hostname = window.location.hostname;

    if (
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname.includes("front")
    ) {
      return "test";
    }

    const parts = hostname.split(".");

    if (parts.length > 2) {
      if (parts[0].toLowerCase() === "www") {
        return parts.length > 3 ? parts[1] : "test";
      }
      return parts[0];
    }

    return "test";
  };

  const headers = {
    "X-Tenant": getSubdomain(),
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  // storeIdParam takes precedence; fall back to the currently selected store
  const resolvedStoreId = storeIdParam ?? currentStoreId;
  if (resolvedStoreId) {
    headers["X-Store-Id"] = resolvedStoreId;
  }

  return headers;
};

export const API_BASE_URL_DEV = "https://app.totalclient.xyz/api/v1";
export const API_BASE_URL_UAT = "https://app.totalclient.xyz/api/v1";
export const API_BASE_URL_PROD = "https://app.easyretailflow.com/api/v1";

const getBaseUrl = () => {
  if (import.meta.env.MODE === "development") {
    return API_BASE_URL_DEV;
  }

  const hostname = window.location.hostname;
  // UAT environment uses .xyz domain
  if (hostname.includes("totalclient.xyz")) {
    return API_BASE_URL_UAT;
  }
  // Production environment uses .erf domain
  return API_BASE_URL_PROD;
};

export const API_BASE_URL = getBaseUrl();

export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: `${API_BASE_URL}/login`,
    LOGOUT: `${API_BASE_URL}/logout`,
    ME: `${API_BASE_URL}/me`,
    PROFILE: `${API_BASE_URL}/profile`,
    CHANGE_PASSWORD: (id) => `${API_BASE_URL}/user/${id}/change-password`,
  },
  DASHBOARD: {
    INIT: `${API_BASE_URL}/init-page/dashboard`,
  },
  USERS: {
    EMPLOYEES: `${API_BASE_URL}/employee`,
    EMPLOYEE_DETAILS: (id) => `${API_BASE_URL}/employee/${id}`,
    DESIGNATIONS: `${API_BASE_URL}/employee_designation`,
    ROLES: `${API_BASE_URL}/roles`,
    PERMISSIONS: `${API_BASE_URL}/permissions`,
    USERS: `${API_BASE_URL}/user`,
    VERIFY_PIN: `${API_BASE_URL}/employee/verify-pin`,
  },
  INVENTORY: {
    PRODUCTS: `${API_BASE_URL}/product`,
    PRODUCT_DETAILS: (id) => `${API_BASE_URL}/product/${id}`,
    UNITS: `${API_BASE_URL}/unit`,
    UNIT_DETAILS: (id) => `${API_BASE_URL}/unit/${id}`,
    BRANDS: `${API_BASE_URL}/brand`,
    BRAND_DETAILS: (id) => `${API_BASE_URL}/brand/${id}`,
    CATEGORIES: `${API_BASE_URL}/category_product`,
    CATEGORY_DETAILS: (id) => `${API_BASE_URL}/category_product/${id}`,
    CATEGORIES_REORDER: `${API_BASE_URL}/category-products/reorder`,
    MODELS: `${API_BASE_URL}/brand-models`,
    BRAND_MODELS: (id) => `${API_BASE_URL}/brand/${id}/model`,
    MODEL_DETAILS: (brandId, modelId) =>
      `${API_BASE_URL}/brand/${brandId}/model/${modelId}`,
    INVENTORY: `${API_BASE_URL}/inventory`,
    INVENTORY_DETAILS: (id) => `${API_BASE_URL}/inventory/${id}`,

    // New - Core Inventory
    LOW_STOCK: `${API_BASE_URL}/inventory/low-stock`,
    BY_STORE: (storeId) => `${API_BASE_URL}/inventory/store/${storeId}`,
    BY_PRODUCT: (productId) => `${API_BASE_URL}/inventory/product/${productId}`,
    STATS: `${API_BASE_URL}/inventory/stats`,
    VALUATION: `${API_BASE_URL}/inventory/valuation`,

    // New - Batches
    BATCHES: `${API_BASE_URL}/inventory-batches`,
    BATCH_DETAILS: (id) => `${API_BASE_URL}/inventory-batches/${id}`,
    BATCHES_AVAILABLE: (productId, storeId) =>
      `${API_BASE_URL}/inventory-batches/available/${productId}/${storeId}`,
    BATCHES_HISTORY: (productId, storeId) =>
      `${API_BASE_URL}/inventory-batches/history/${productId}/${storeId}`,
    BATCHES_SEARCH: `${API_BASE_URL}/inventory-batches/search`,

    // Updated - Serials
    SERIALS: `${API_BASE_URL}/inventory-serials`,
    SERIAL_DETAILS: (id) => `${API_BASE_URL}/inventory-serials/${id}`,
    SERIAL_TRACK: (serialNumber) =>
      `${API_BASE_URL}/inventory-serials/track/${serialNumber}`,
    SERIALS_AVAILABLE: `${API_BASE_URL}/inventory-serials/available`,
    SERIALS_BULK_CREATE: `${API_BASE_URL}/inventory-serials/bulk-create`,
    SERIALS_BULK_STATUS: `${API_BASE_URL}/inventory-serials/bulk-status`,

    // New - Transactions
    TRANSACTIONS: `${API_BASE_URL}/inventory-transactions`,
    TRANSACTIONS_SUMMARY: (inventoryId) =>
      `${API_BASE_URL}/inventory-transactions/summary/${inventoryId}`,
    TRANSACTIONS_BATCH: (batchId) =>
      `${API_BASE_URL}/inventory-transactions/batch/${batchId}`,
    TRANSACTIONS_SERIAL: (serialNo) =>
      `${API_BASE_URL}/inventory-transactions/serial/${serialNo}`,

    // Barcode
    BARCODE_SCAN: `${API_BASE_URL}/inventory/barcode/scan`,
    BARCODE_GENERATE: `${API_BASE_URL}/inventory/barcode/generate`,

    EXPORT_PRODUCTS: `${API_BASE_URL}/export/products`,
    IMPORT_PRODUCTS: `${API_BASE_URL}/import/products`,
    IMPORT_TEMPLATE: `${API_BASE_URL}/import/products/template`,
    DOWNLOAD_EXPORT: (filename) =>
      `${API_BASE_URL}/export/products/download/${filename}`,
    POS_PRODUCTS: `${API_BASE_URL}/pos/product`,
    POS_PRODUCT_DETAILS: (id) => `${API_BASE_URL}/pos/product/${id}`,
    TAXES: `${API_BASE_URL}/tax`,
  },
  SALES: {
    SALES: `${API_BASE_URL}/sale`,
    SALE_DETAILS: (id) => `${API_BASE_URL}/sale/${id}`,
    ROLLBACK: (id) => `${API_BASE_URL}/sale/${id}/rollback`,
    SALE_ITEM: (id) => `${API_BASE_URL}/sale_item/${id}`,
    SALE_ITEM_BULK: `${API_BASE_URL}/sale_item/bulk`,
    CUSTOMERS: `${API_BASE_URL}/customer`,
    CUSTOMER_NOTES: `${API_BASE_URL}/customer_note`,
    CUSTOMER_NOTE_DETAILS: (id) => `${API_BASE_URL}/customer_note/${id}`,
    CUSTOMER_GROUPS: `${API_BASE_URL}/customer_group`,
    PAYMENTS: `${API_BASE_URL}/payment`,
    PAYMENT_DETAILS: (id) => `${API_BASE_URL}/payment/${id}`,
    CUSTOMER_DETAILS: (id) => `${API_BASE_URL}/customer/${id}`,
    POS_CART: `${API_BASE_URL}/pos/cart`,
    POS_CART_DETAILS: (id) => `${API_BASE_URL}/pos/cart/${id}`,
    POS_CART_ITEMS: (id) => `${API_BASE_URL}/pos/cart/${id}/item`,
    POS_CART_ITEMS_BULK: (id) => `${API_BASE_URL}/pos/cart/${id}/cart.item/bulk`,
    POS_CART_CLEAR_ITEMS: (id) => `${API_BASE_URL}/pos/cart/${id}/cart.item`,
    POS_CART_ITEM_DETAILS: (cartId, itemId) =>
      `${API_BASE_URL}/pos/cart/${cartId}/item/${itemId}`,
    POS_INIT: `${API_BASE_URL}/pos/init`,
    POS_CUSTOMERS: `${API_BASE_URL}/pos/customer`,
    POS_CART_COMPLETE: (id) => `${API_BASE_URL}/pos/cart/${id}/complete`,
    POS_SALE_PAYMENT: (id) => `${API_BASE_URL}/pos/sale/${id}/payment`,
    NOTIFICATION_SALES_INVOICE: (id) =>
      `${API_BASE_URL}/notifications/email/sales/${id}/invoice`,
    SEND_INVOICE: (id) => `${API_BASE_URL}/sale/${id}/send-invoice`,
    NOTIFICATION_CUSTOMER_REG: (id) =>
      `${API_BASE_URL}/notifications/email/customer/${id}/registration`,
    IMPORT_CUSTOMERS: `${API_BASE_URL}/import/customers`,
    IMPORT_CUSTOMERS_TEMPLATE: `${API_BASE_URL}/import/customers/template`,
  },
  PURCHASES: {
    ORDERS: `${API_BASE_URL}/purchase_order`,
    ORDER_DETAILS: (id) => `${API_BASE_URL}/purchase_order/${id}`,
    ORDER_ITEMS: (id) => `${API_BASE_URL}/purchase_order/${id}/item`,
    ORDER_ITEM_DETAILS: (orderId, itemId) =>
      `${API_BASE_URL}/purchase_order/${orderId}/item/${itemId}`,
    GRN: `${API_BASE_URL}/goods_received_note`,
    GRN_DETAILS: (id) => `${API_BASE_URL}/goods_received_note/${id}`,
    GRN_STATUS: (id) => `${API_BASE_URL}/goods_received_note/${id}/status`,
    GRN_ITEMS: (id) => `${API_BASE_URL}/goods_received_note/${id}/item`,
    GRN_ITEM_DETAILS: (grnId, itemId) =>
      `${API_BASE_URL}/goods_received_note/${grnId}/item/${itemId}`,
    SUPPLIERS: `${API_BASE_URL}/supplier`,
  },
  STOCK: {
    ADJUSTMENTS: `${API_BASE_URL}/stock-adjustments`,
    PRODUCT_HISTORY: `${API_BASE_URL}/stock-adjustments/product/history`,
    OPENING_BALANCE: `${API_BASE_URL}/stock-adjustments/opening-balance`,
    ADJUSTMENT_DETAILS: (id) => `${API_BASE_URL}/stock-adjustments/${id}`,
    PENDING_APPROVALS: `${API_BASE_URL}/stock-adjustments/pending/approvals`,
    CANCEL: (id) => `${API_BASE_URL}/stock-adjustments/${id}/cancel`,
    PROCESS: (id) => `${API_BASE_URL}/stock-adjustments/${id}/process`,
    SUBMIT: (id) => `${API_BASE_URL}/stock-adjustments/${id}/submit`,
    APPROVAL: (id) => `${API_BASE_URL}/stock-adjustments/${id}/approval`,
    VALIDATE: (id) => `${API_BASE_URL}/stock-adjustments/${id}/validate`,

    // New
    BULK_PROCESS: `${API_BASE_URL}/stock-adjustments/bulk/process`,
    STATISTICS: `${API_BASE_URL}/stock-adjustments/statistics`,
    BY_STORE: (storeId) => `${API_BASE_URL}/stock-adjustments/store/${storeId}`,
    EXPORT: `${API_BASE_URL}/stock-adjustments/export`,
    DASHBOARD: `${API_BASE_URL}/stock-adjustments/dashboard`,

    // Transfers (new section)
    TRANSFERS: `${API_BASE_URL}/stock-transfers`,
    TRANSFER_DETAILS: (id) => `${API_BASE_URL}/stock-transfers/${id}`,
    TRANSFER_PROCESS: (id) => `${API_BASE_URL}/stock-transfers/${id}/process`,
    TRANSFER_RECEIVE: (id) => `${API_BASE_URL}/stock-transfers/${id}/receive`,
    TRANSFER_CANCEL: (id) => `${API_BASE_URL}/stock-transfers/${id}/cancel`,
    TRANSFER_SUBMIT: (id) => `${API_BASE_URL}/stock-transfers/${id}/submit`,
    TRANSFER_APPROVAL: (id) => `${API_BASE_URL}/stock-transfers/${id}/approval`,
    TRANSFER_STATISTICS: `${API_BASE_URL}/stock-transfers/statistics`,
    TRANSFER_VALIDATE: (id) => `${API_BASE_URL}/stock-transfers/${id}/validate`,
    TRANSFER_BULK_PROCESS: `${API_BASE_URL}/stock-transfers/bulk/process`,
  },
  REPAIRS: {
    JOBS: `${API_BASE_URL}/repair-jobs`,
    REPAIR_DETAILS: (id) => `${API_BASE_URL}/repair-jobs/${id}`,
    DEVICE_PASSWORD: (id) =>
      `${API_BASE_URL}/repair-jobs/${id}/device-password`,
    STATUS_UPDATE: (id) => `${API_BASE_URL}/repair-jobs/${id}/status`,
    PARTS: (id) => `${API_BASE_URL}/repair-jobs/${id}/parts`,
    LABOR: (id) => `${API_BASE_URL}/repair-jobs/${id}/labor`,
    CATEGORIES: `${API_BASE_URL}/category_repair`,
    PREDEFINED_JOBS: `${API_BASE_URL}/predefined-repair-jobs`,
    PREDEFINED_JOB_DETAILS: (id) =>
      `${API_BASE_URL}/predefined-repair-jobs/${id}`,
    TOGGLE_ACTIVE: (id) =>
      `${API_BASE_URL}/predefined-repair-jobs/${id}/toggle-active`,
    CREATE_JOB: (id) =>
      `${API_BASE_URL}/predefined-repair-jobs/${id}/create-repair-job`,
    SPECIAL_PARTS: `${API_BASE_URL}/special-parts`,
    REPAIR_JOB_SPECIAL_PARTS: (jobId) =>
      `${API_BASE_URL}/repair-jobs/${jobId}/special-parts`,
    MODELS_BY_BRAND: (brandId) => `${API_BASE_URL}/brand/${brandId}/model`,
    PAYMENTS: (id) => `${API_BASE_URL}/repair-jobs/${id}/payments`,
  },
  EXPENSES: {
    EXPENSES: `${API_BASE_URL}/expenses`,
    EXPENSE_DETAILS: (id) => `${API_BASE_URL}/expenses/${id}`,
    CATEGORIES: `${API_BASE_URL}/expense-categories`,
    OVERVIEW: `${API_BASE_URL}/expenses/statistics/overview`,
    SUBMIT: (id) => `${API_BASE_URL}/expenses/${id}/submit-for-approval`,
    MARK_PAID: (id) => `${API_BASE_URL}/expenses/${id}/mark-as-paid`,
    APPROVE: (id) => `${API_BASE_URL}/expenses/${id}/approve`,
    REJECT: (id) => `${API_BASE_URL}/expenses/${id}/reject`,
  },
  PAYMENT_METHODS: {
    LIST: `${API_BASE_URL}/payment-methods`,
    DETAILS: (id) => `${API_BASE_URL}/payment-methods/${id}`,
    AVAILABLE: `${API_BASE_URL}/payment-methods/available`,
  },
  PRINTERS: {
    LIST: `${API_BASE_URL}/printers`,
    DETAILS: (id) => `${API_BASE_URL}/printers/${id}`,
    SET_DEFAULT: (id) => `${API_BASE_URL}/printers/${id}/set-default`,
    PAPER_SIZES: (printerId) => `${API_BASE_URL}/printers/${printerId}/paper-sizes`,
    PAPER_SIZE_DETAILS: (printerId, paperId) =>
      `${API_BASE_URL}/printers/${printerId}/paper-sizes/${paperId}`,
  },
  PAPER_SIZES: {
    LIST: (printerId) =>
      printerId
        ? `${API_BASE_URL}/printers/${printerId}/paper-sizes`
        : `${API_BASE_URL}/paper-sizes`,
    DETAILS: (printerId, paperId) =>
      printerId
        ? `${API_BASE_URL}/printers/${printerId}/paper-sizes/${paperId}`
        : `${API_BASE_URL}/paper-sizes/${paperId}`,
    TEMPLATES: (paperSizeId) =>
      `${API_BASE_URL}/paper-sizes/${paperSizeId}/templates`,
  },
  PRINTER_TEMPLATES: {
    LIST: (paperSizeId) =>
      `${API_BASE_URL}/paper-sizes/${paperSizeId}/templates`,
    BULK_ASSIGN: (paperSizeId) =>
      `${API_BASE_URL}/paper-sizes/${paperSizeId}/templates`,
  },
  MEDIA: {
    MEDIA: `${API_BASE_URL}/media`,
    MEDIA_DETAILS: (id) => `${API_BASE_URL}/media/${id}`,
    UPLOAD: `${API_BASE_URL}/media/upload`,
    ASSIGN: `${API_BASE_URL}/media-library/assign`,
    REMOVE: `${API_BASE_URL}/media-library/remove`,
  },
  SETTINGS: {
    TENANT: `${API_BASE_URL}/tenant-setting`,
    TENANT_DETAILS: (id) => `${API_BASE_URL}/tenant-setting/${id}`,
    RESET: `${API_BASE_URL}/settings/reset`,
  },
  TEMPLATES: {
    TEMPLATES: `${API_BASE_URL}/templates`,
    TEMPLATE_DETAILS: (id) => `${API_BASE_URL}/templates/${id}`,
    PLACEHOLDERS: `${API_BASE_URL}/templates/placeholders`,
    TYPES: `${API_BASE_URL}/templates/types`,
    TYPE_PREVIEW: (type) => `${API_BASE_URL}/templates/types/${type}/preview`,
    PREVIEW: (id) =>
      id
        ? `${API_BASE_URL}/templates/${id}/preview`
        : `${API_BASE_URL}/templates/preview`,
    PREVIEW_SAVED: (id) => `${API_BASE_URL}/templates/${id}/preview`,
    PREVIEW_DRAFT: `${API_BASE_URL}/templates/preview`,
    LEGACY_PREVIEW: (id) => `${API_BASE_URL}/templates/preview/${id || "null"}`,
  },
  REPORTS: {
    // PDF Reports
    PRODUCT_BARCODE: `${API_BASE_URL}/reports/pdf/product-barcode-report`,
    SERIAL_BARCODE: `${API_BASE_URL}/reports/pdf/serial-barcode-report`,
    STOCK_REPORT: `${API_BASE_URL}/reports/pdf/product-stock-report`,
    SALES_SUMMARY: `${API_BASE_URL}/reports/pdf/sales-summary-report`,
    PARTS_USAGE: `${API_BASE_URL}/reports/pdf/parts-usage-report`,
    SUPPLIERWISE_EXPENSES: `${API_BASE_URL}/reports/pdf/supplierwise-expense-report`,
    STOCK_TRANSFER: `${API_BASE_URL}/reports/pdf/stock-transfer-report`,
    SALES_INVOICE_REPORT: `${API_BASE_URL}/reports/pdf/sales-invoice-report`,
    PROFIT_LOSS: `${API_BASE_URL}/reports/pdf/profit-loss-report`,
    INVENTORY_MOVEMENT: `${API_BASE_URL}/reports/pdf/inventory-movement-report`,
    EXPENSE: `${API_BASE_URL}/reports/pdf/expense-report`,
    DAILY_CASH_BALANCE: `${API_BASE_URL}/reports/pdf/daily-cash-balance-report`,
    CUSTOMERWISE_SALES: `${API_BASE_URL}/reports/pdf/customerwise-sales-report`,

    SALES_INVOICE: (id) => `${API_BASE_URL}/reports/pdf/sales/${id}/invoice`,
    SALES_RECEIPT: (id) => `${API_BASE_URL}/reports/pdf/sales/${id}/receipt`,
    PURCHASE_INVOICE: (id) =>
      `${API_BASE_URL}/reports/pdf/purchases/${id}/invoice`,
    REPAIR_INVOICE: (id) => `${API_BASE_URL}/reports/pdf/repairs/${id}/invoice`,
    REPAIR_JOB_NOTE: (id) =>
      `${API_BASE_URL}/reports/pdf/repairs/${id}/job-note`,
    REPAIR_JOB_LABEL: (id) =>
      `${API_BASE_URL}/reports/pdf/repairs/${id}/job-label`,
    GRN_REPORT: (id) =>
      `${API_BASE_URL}/reports/pdf/goods-received-notes/${id}`,
    PURCHASE_ORDER_REPORT: (id) =>
      `${API_BASE_URL}/reports/pdf/purchase-orders/${id}`,
    STOCK_TRANSFER_DOCUMENT: (id) =>
      `${API_BASE_URL}/reports/pdf/stock-transfers/${id}`,
    QUOTATION_PDF: (id) => `${API_BASE_URL}/reports/pdf/quotations/${id}`,
    WARRANTY_INTAKE: (id) =>
      `${API_BASE_URL}/reports/pdf/warranty-claims/${id}/intake`,
    WARRANTY_REJECTION: (id) =>
      `${API_BASE_URL}/reports/pdf/warranty-claims/${id}/rejection`,
    WARRANTY_EXCHANGE: (id) =>
      `${API_BASE_URL}/reports/pdf/warranty-claims/${id}/exchange`,
    WARRANTY_COMPLETION: (id) =>
      `${API_BASE_URL}/reports/pdf/warranty-claims/${id}/completion`,
    CREDIT_NOTE: (id) =>
      `${API_BASE_URL}/reports/pdf/credit-notes/${id}`,

    // JSON Data Endpoints
    DATA: {
      SUPPLIERWISE_EXPENSES: `${API_BASE_URL}/reports/data/supplierwise-expense-report`,
      STOCK_TRANSFER: `${API_BASE_URL}/reports/data/stock-transfer-report`,
      STOCK_REPORT: `${API_BASE_URL}/reports/data/product-stock-report`,
      SALES_SUMMARY: `${API_BASE_URL}/reports/data/sales-summary-report`,
      SALES_INVOICE: `${API_BASE_URL}/reports/data/sales-invoice-report`,
      PROFIT_LOSS: `${API_BASE_URL}/reports/data/profit-loss-report`,
      PARTS_USAGE: `${API_BASE_URL}/reports/data/parts-usage-report`,
      INVENTORY_MOVEMENT: `${API_BASE_URL}/reports/data/inventory-movement-report`,
      EXPENSE: `${API_BASE_URL}/reports/data/expense-report`,
      DAILY_CASH_BALANCE: `${API_BASE_URL}/reports/data/daily-cash-balance-report`,
      CUSTOMERWISE_SALES: `${API_BASE_URL}/reports/data/customerwise-sales-report`,
    },
  },
  QUOTATIONS: {
    QUOTATIONS: `${API_BASE_URL}/quotation`,
    QUOTATION_DETAILS: (id) => `${API_BASE_URL}/quotation/${id}`,
    QUOTATION_STATUS: (id) => `${API_BASE_URL}/quotations/${id}/status`,
    CONVERT_TO_SALE: (id) => `${API_BASE_URL}/quotations/${id}/convert-to-sale`,
  },
  STORES: {
    STORES: `${API_BASE_URL}/store`,
    STORE_DETAILS: (id) => `${API_BASE_URL}/store/${id}`,
    STORE_EMPLOYEE: `${API_BASE_URL}/store_employee`,
    STORE_PRODUCT: `${API_BASE_URL}/store_product`,
  },
  MISC: {
    DAILY_TASKS: `${API_BASE_URL}/tasks/daily`,
    TEST: `${API_BASE_URL}/test`,
    TAXES: `${API_BASE_URL}/tax`,
  },
  RECURRING_INVOICES: {
    RECURRING_INVOICES: `${API_BASE_URL}/recurring-invoices`,
    RECURRING_INVOICE_DETAILS: (id) => `${API_BASE_URL}/recurring-invoices/${id}`,
    PAUSE: (id) => `${API_BASE_URL}/recurring-invoices/${id}/pause`,
    RESUME: (id) => `${API_BASE_URL}/recurring-invoices/${id}/resume`,
    GENERATE: (id) => `${API_BASE_URL}/recurring-invoices/${id}/generate`,
    ITEMS: (id) => `${API_BASE_URL}/recurring-invoices/${id}/items`,
    ITEM_DETAILS: (invoiceId, itemId) => `${API_BASE_URL}/recurring-invoices/${invoiceId}/items/${itemId}`,
  },
  ASSEMBLE: {
    CONFIGS: `${API_BASE_URL}/assemble-configs`,
    CONFIG_DETAILS: (id) => `${API_BASE_URL}/assemble-configs/${id}`,
    CONFIG_ACTIVATE: (id) => `${API_BASE_URL}/assemble-configs/${id}/activate`,
    CONFIG_ARCHIVE: (id) => `${API_BASE_URL}/assemble-configs/${id}/archive`,

    // Jobs
    JOBS: `${API_BASE_URL}/assemble-jobs`,
    JOB_DETAILS: (id) => `${API_BASE_URL}/assemble-jobs/${id}`,
    JOB_CONFIRM: (id) => `${API_BASE_URL}/assemble-jobs/${id}/confirm`,
    JOB_START_BUILDING: (id) => `${API_BASE_URL}/assemble-jobs/${id}/start-building`,
    JOB_SUBMIT_QC: (id) => `${API_BASE_URL}/assemble-jobs/${id}/submit-qc`,
    JOB_COMPLETE: (id) => `${API_BASE_URL}/assemble-jobs/${id}/complete`,
    JOB_DELIVER: (id) => `${API_BASE_URL}/assemble-jobs/${id}/deliver`,
    JOB_CANCEL: (id) => `${API_BASE_URL}/assemble-jobs/${id}/cancel`,
    JOB_CREATE_FROM_CONFIG: (configId) => `${API_BASE_URL}/assemble-configs/${configId}/create-job`,
    JOB_CONVERT_TO_SALE: (id) => `${API_BASE_URL}/assemble-jobs/${id}/convert-to-sale`,
    JOB_CONVERT_TO_QUOTATION: (id) => `${API_BASE_URL}/assemble-jobs/${id}/convert-to-quotation`,
    JOB_STATISTICS: `${API_BASE_URL}/assemble-jobs/statistics`,
  },
  WARRANTY: {
    CLAIMS: `${API_BASE_URL}/warranty-claims`,
    CLAIM_DETAILS: (id) => `${API_BASE_URL}/warranty-claims/${id}`,
    CLAIM_ASSESS: (id) => `${API_BASE_URL}/warranty-claims/${id}/assess`,
    CLAIM_APPROVE: (id) => `${API_BASE_URL}/warranty-claims/${id}/approve`,
    CLAIM_FULFIL: (id) => `${API_BASE_URL}/warranty-claims/${id}/fulfil`,
    CLAIM_RESOLVE: (id) => `${API_BASE_URL}/warranty-claims/${id}/resolve`,
    CLAIM_CLOSE: (id) => `${API_BASE_URL}/warranty-claims/${id}/close`,
    CLAIM_REJECT: (id) => `${API_BASE_URL}/warranty-claims/${id}/reject`,
    SUPPLIER_CLAIM: (id) => `${API_BASE_URL}/warranty-claims/${id}/supplier-claim`,
    ISSUE_DOCUMENT: (id, type) =>
      `${API_BASE_URL}/warranty-claims/${id}/documents/${type}/issue`,
    SIGN_DOCUMENT: (id, documentId) =>
      `${API_BASE_URL}/warranty-claims/${id}/documents/${documentId}/sign`,
    CLAIM_STATUS: (id) => `${API_BASE_URL}/warranty-claims/${id}/status`,
  },
};
